import type { Rect } from '../ink/stroke';
import type { Theme } from './tokens';
import type { PageKind, TemplateId } from '../store/types';

/**
 * Templates draw directly into tile canvases (world-rect given), beneath ink.
 * All are repeating patterns except 'cornell' (fixed pages only), which draws
 * its cue/summary lines relative to the fixed page rect passed in `pageRect`.
 */
const LINE_LIGHT = 'rgba(43,43,51,0.12)', LINE_DARK = 'rgba(236,234,228,0.10)';
const ACCENT_LIGHT = 'rgba(232,146,124,0.35)', ACCENT_DARK = 'rgba(240,164,143,0.30)';
const SPACING = 32; // px between lines/dots at scale 1

export const TEMPLATES: Record<TemplateId, { label: string; repeating: boolean }> = {
  blank:   { label: 'Blank',  repeating: true },
  lined:   { label: 'Lined',  repeating: true },
  grid:    { label: 'Grid',   repeating: true },
  dotted:  { label: 'Dotted', repeating: true },
  cornell: { label: 'Cornell', repeating: false },
};

export function templatesForKind(kind: PageKind): TemplateId[] {
  return (Object.keys(TEMPLATES) as TemplateId[]).filter((t) => kind === 'fixed' || TEMPLATES[t].repeating);
}

/**
 * Draw the template for the world-space rect `world` into a ctx whose
 * transform already maps world→canvas at `scale`. `pageRect` is the fixed
 * page bounds (needed by cornell); pass the world rect for infinite pages.
 */
export function drawTemplate(
  ctx: CanvasRenderingContext2D,
  id: TemplateId,
  theme: Theme,
  world: Rect,
  scale: number,
  pageRect?: Rect,
): void {
  const line = theme === 'dark' ? LINE_DARK : LINE_LIGHT;
  ctx.save();
  ctx.strokeStyle = line;
  ctx.fillStyle = line;
  ctx.lineWidth = 1 / scale;
  const x0 = Math.floor(world.x / SPACING) * SPACING;
  const y0 = Math.floor(world.y / SPACING) * SPACING;
  const x1 = world.x + world.w, y1 = world.y + world.h;
  if (id === 'lined' || id === 'grid' || id === 'cornell') {
    for (let y = y0; y <= y1; y += SPACING) {
      ctx.beginPath(); ctx.moveTo(world.x, y); ctx.lineTo(x1, y); ctx.stroke();
    }
  }
  if (id === 'grid') {
    for (let x = x0; x <= x1; x += SPACING) {
      ctx.beginPath(); ctx.moveTo(x, world.y); ctx.lineTo(x, y1); ctx.stroke();
    }
  }
  if (id === 'dotted') {
    const r = 1.2 / scale;
    for (let y = y0; y <= y1; y += SPACING) {
      for (let x = x0; x <= x1; x += SPACING) {
        ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
      }
    }
  }
  if (id === 'cornell' && pageRect) {
    ctx.strokeStyle = theme === 'dark' ? ACCENT_DARK : ACCENT_LIGHT;
    ctx.lineWidth = 1.5 / scale;
    const cueX = pageRect.x + pageRect.w * 0.28;
    const summaryY = pageRect.y + pageRect.h * 0.82;
    ctx.beginPath(); ctx.moveTo(cueX, pageRect.y); ctx.lineTo(cueX, summaryY); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(pageRect.x, summaryY); ctx.lineTo(pageRect.x + pageRect.w, summaryY); ctx.stroke();
  }
  ctx.restore();
}
